import React, { useCallback, useRef, useState } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Tag from '@amzn/meridian/tag';
import Popover from '@amzn/meridian/popover';
import Divider from '@amzn/meridian/divider';
import { TEXTS } from '../../utils/constants';
import { formatEpochAsHHMM } from './FlowPlannerModel';
import { TYPE_TO_TAG } from '../../utils/laborOpportunityConstants';
import './shared/flowPlannerShared.css';

const INLINE_LABEL_MIN_WIDTH_PCT = 6;
const POPOVER_WIDTH = 260;

/**
 * One bar inside the Opportunity Timeline. Renders an accessible button that
 * reveals a popover on hover/focus with the opportunity's time window,
 * headcount, and full work-group list.
 */
const OpportunityBar = ({ opportunity, range, leftPct, widthPct, widthValue }) => {
  const anchorRef = useRef();
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  const headcount = opportunity.headcount || 0;
  const showInline = widthValue >= INLINE_LABEL_MIN_WIDTH_PCT;
  const tags = Array.isArray(opportunity.workgroupTags) ? opportunity.workgroupTags : [];
  const startLabel = formatEpochAsHHMM(range.start);
  const endLabel = formatEpochAsHHMM(range.end);

  const wrapperStyle = { '--bar-left': leftPct, '--bar-width': widthPct };
  const fillClass = `opportunity-bar-fill${open ? ' is-active' : ''}`;

  return (
    <div className="opportunity-bar" style={wrapperStyle}>
      <button
        ref={anchorRef}
        type="button"
        className="opportunity-bar-button"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        aria-label={`${opportunity.opportunityType} ${headcount} HC, ${startLabel} to ${endLabel}`}>
        <div className={fillClass} data-type={opportunity.opportunityType}>
          {showInline ? `${headcount}` : ''}
        </div>
      </button>
      <Popover anchorNode={anchorRef.current} open={open} onClose={close} position="top" spacingInset="400">
        <Column spacing="200" width={POPOVER_WIDTH}>
          <Row spacing="200" alignmentVertical="center" alignmentHorizontal="justify">
            <Tag type={TYPE_TO_TAG[opportunity.opportunityType] || 'neutral'}>{opportunity.opportunityType}</Tag>
            <Text type={TEXTS.T3} weight="bold">{`${headcount} HC`}</Text>
          </Row>
          <Text type={TEXTS.T3} color="secondary">{`${startLabel} – ${endLabel}`}</Text>
          {tags.length > 0 && (
            <>
              <Divider />
              <Text type={TEXTS.T4} weight="bold" color="secondary">
                Work Groups
              </Text>
              <Column spacing="100">
                {tags.map((tag) => (
                  <Text key={tag} type={TEXTS.T3}>
                    {tag}
                  </Text>
                ))}
              </Column>
            </>
          )}
        </Column>
      </Popover>
    </div>
  );
};

export default OpportunityBar;
